var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { DexfSettingsUtility, LocalizedInformationAlternateLanguage } from '@inter-ikea-kompis/utilities';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';
import { PopoverDirectionEnum, PopoverPadding } from '@inter-ikea-kompis/component-popover';
import { Popover } from '@inter-ikea-kompis/component-popover';
import { Sheet } from '@inter-ikea-kompis/component-sheet';
import { SheetBody, SheetBodyPadding, SheetFooter, SheetHeader } from '@inter-ikea-kompis/component-sheet';
import { Text } from '@inter-ikea-kompis/component-text';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import { globe, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import LanguageSelectorLayoutEnum from '../../enums/LanguageSelectorLayoutEnum.js';
import LanguageSelectorModalEnum from '../../enums/LanguageSelectorModalEnum.js';
import LanguageSelectorSizeEnum from '../../enums/LanguageSelectorSizeEnum.js';
import LanguageSelectorCard from '../language-selector-card/LanguageSelectorCard.js';
import LanguageSelectorLinkGroup from '../language-selector-link-group/LanguageSelectorLinkGroup.js';
import LanguageSelectorStyle from './LanguageSelectorStyle.js';
/**
 * LanguageSelector class.
 *
 * @event change Triggered when a new language is selected.
 * @event modalOpen Triggered when a modal should be opened.
 * @event modalClose Triggered when a modal should be closed.
 */
export default class LanguageSelector extends KompisElement {
    constructor() {
        super(...arguments);
        // Layout.
        this.layout = LanguageSelectorLayoutEnum.popover;
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Modal visible
        this.visibleModal = null;
        // Sets the button size of the Language Selector button. Note that this doesn't change the size when layout is set to "link".
        this.buttonSize = LanguageSelectorSizeEnum.small;
        this.listeners = {
            hideKompisPopover: () => this.hideKompisPopover()
        };
        this.sheetElement = null;
        this.dexfSettings = null;
    }
    render() {
        // We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `
			<style>
				${LanguageSelectorStyle.getStyle({
            theme: this.theme
        })}
			</style>
			${this.getLayout()}
		`;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.closeSheet();
        this.hideKompisPopover();
    }
    async connectedCallback() {
        registerIcon(globe);
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('kompis-popover', Popover);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-popover-padding', PopoverPadding);
        this.customElements.define('kompis-sheet', Sheet);
        this.customElements.define('kompis-sheet-header', SheetHeader);
        this.customElements.define('kompis-sheet-body', SheetBody);
        this.customElements.define('kompis-sheet-footer', SheetFooter);
        this.customElements.define('kompis-sheet-body-padding', SheetBodyPadding);
        this.customElements.define('kompis-language-selector-card', LanguageSelectorCard);
        this.customElements.define('kompis-language-selector-link-group', LanguageSelectorLinkGroup);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        if (changedProperties.has('visibleModal')) {
            if (this.visibleModal === LanguageSelectorModalEnum.languageSelectorSheet) {
                this.openSheet();
            }
            else {
                this.closeSheet();
            }
        }
        this.forwardPropsToCard();
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation?.translations;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    getSettings() {
        return this.dexfSettings;
    }
    /**
     * Gets layout.
     *
     * @returns Template.
     */
    getLayout() {
        if (this.layout === LanguageSelectorLayoutEnum.sheet ||
            this.layout === LanguageSelectorLayoutEnum.popover) {
            return this.getButtonLayout();
        }
        else {
            return this.getLinkLayout();
        }
    }
    /**
     * Gets button layout.
     *
     */
    getButtonLayout() {
        return html `${this.getButton()} ${this.getKompisPopover()} `;
    }
    /**
     * Gets button for buttonLayout.
     *
     */
    getButton() {
        const options = LocalizedInformationAlternateLanguage.getAlternateLanguages(this.getSettings(), this.locale);
        if (options.length <= 1) {
            return null;
        }
        const secondaryVariant = 'secondary';
        return html `
			<skapa-button
				aria-label="${this.getTranslations().languageSelectorExplanatoryLabel}"
				@click="${() => this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: {
                visibleModal: this.layout === LanguageSelectorLayoutEnum.popover
                    ? LanguageSelectorModalEnum.languageSelectorPopover
                    : LanguageSelectorModalEnum.languageSelectorSheet
            }
        }))}"
				size="${this.buttonSize === LanguageSelectorSizeEnum.small ? 'small' : 'medium'}"
				variant="${secondaryVariant}"
			>
				<skapa-icon slot="icon" icon="globe"></skapa-icon>
				${options.find((option) => option.locale === this.locale)?.label}
			</skapa-button>
		`;
    }
    /**
     * Gets link layout.
     *
     * @returns Template.
     */
    getLinkLayout() {
        return html `
			<kompis-language-selector-link-group
				@change="${(event) => this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: event.detail }))}"
				.theme="${this.theme}"
				.settings="${this.settings}"
				.localizedInformation="${this.localizedInformation}"
				.locale="${this.locale}"
			>
			</kompis-language-selector-link-group>
		`;
    }
    /**
     * Closes the sheet.
     */
    closeSheet() {
        if (this.sheetElement) {
            this.sheetElement.visible = false;
            this.sheetElement = null;
        }
    }
    /**
     * Opens the sheet.
     */
    openSheet() {
        if (this.sheetElement) {
            return;
        }
        const sheetElement = this.createSheetElement();
        const onClose = () => {
            if (this.sheetElement) {
                this.dispatchEvent(new CustomEvent('modalClose', {
                    bubbles: true,
                    detail: {
                        visibleModal: null
                    }
                }));
            }
        };
        // When user clicks 'close'
        sheetElement.addEventListener('overlayClick', onClose);
        sheetElement.addEventListener('closeButtonClick', onClose);
        // Cleanup sheet when the animation is over
        sheetElement.addEventListener('animationEnd', () => {
            if (!sheetElement.visible) {
                sheetElement?.parentElement?.removeChild(sheetElement);
            }
        });
        this.sheetElement = sheetElement;
        sheetElement.visible = true;
        const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
        parent.appendChild(sheetElement);
    }
    /**
     * Create a sheet element.
     */
    createSheetElement() {
        const sheetElement = this.createInternalElement('kompis-sheet');
        const sheetHeaderElement = this.createInternalElement('kompis-sheet-header');
        const sheetBodyElement = this.createInternalElement('kompis-sheet-body');
        const sheetPaddingElement = this.createInternalElement('kompis-sheet-body-padding');
        sheetElement.theme = this.theme;
        sheetBodyElement.theme = this.theme;
        sheetPaddingElement.theme = this.theme;
        sheetPaddingElement.style.padding = this.theme.spacing.space150 / 16 + 'rem';
        sheetHeaderElement.theme = this.theme;
        sheetHeaderElement.heading = this.getTranslations().languagePickerLabel;
        sheetHeaderElement.ariaLabelCloseButton = this.getTranslations().modalAriaLabelButtonClose;
        this.cardElement = this.createInternalElement('kompis-language-selector-card');
        this.forwardPropsToCard();
        this.cardElement.addEventListener('change', (event) => {
            this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: event.detail }));
        });
        sheetElement.appendChild(sheetHeaderElement);
        sheetElement.appendChild(sheetBodyElement);
        sheetBodyElement.appendChild(sheetPaddingElement);
        sheetPaddingElement.appendChild(this.cardElement);
        return sheetElement;
    }
    forwardPropsToCard() {
        if (this.cardElement) {
            this.cardElement.theme = this.theme;
            this.cardElement.settings = this.settings;
            this.cardElement.localizedInformation = this.localizedInformation;
            this.cardElement.locale = this.locale;
        }
    }
    /**
     * Gets popover.
     *
     * @returns Template.
     */
    getKompisPopover() {
        if (this.visibleModal !== LanguageSelectorModalEnum.languageSelectorPopover) {
            return null;
        }
        // Event handler click.
        document.body.addEventListener('click', this.listeners.hideKompisPopover);
        return html `
			<kompis-popover
				.visible="${this.visibleModal === LanguageSelectorModalEnum.languageSelectorPopover}"
				.theme="${this.theme}"
				.direction="${PopoverDirectionEnum.bottom}"
			>
				<kompis-popover-padding .theme="${this.theme}">
					<kompis-text
						.theme="${this.theme}"
						class="marginBottom"
						.type="${ThemeFontStyleTypeEnum.headingSmall}"
					>
						${this.getTranslations().languagePickerLabel}
					</kompis-text>
					<div class="spacer"></div>
					<kompis-language-selector-card
						@change="${(event) => this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: event.detail }))}"
						.theme="${this.theme}"
						.settings="${this.settings}"
						.localizedInformation="${this.localizedInformation}"
						.locale="${this.locale}"
					>
					</kompis-language-selector-card>
				</kompis-popover-padding>
			</kompis-popover>
		`;
    }
    /**
     * Event handler click.
     */
    hideKompisPopover() {
        this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } }));
        document.body.removeEventListener('click', this.listeners.hideKompisPopover);
    }
}
__decorate([
    property({ type: String })
], LanguageSelector.prototype, "layout", void 0);
__decorate([
    property({ type: String, required: true })
], LanguageSelector.prototype, "locale", void 0);
__decorate([
    property({ type: Object })
], LanguageSelector.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], LanguageSelector.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], LanguageSelector.prototype, "settings", void 0);
__decorate([
    property({ type: String })
], LanguageSelector.prototype, "visibleModal", void 0);
__decorate([
    property({ type: Object, required: true })
], LanguageSelector.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], LanguageSelector.prototype, "buttonSize", void 0);
//# sourceMappingURL=LanguageSelector.js.map